import React, { useEffect, useState } from 'react'
import DOMPurify from 'dompurify'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../contexts/ToastContext'
import { supabase, ShopConfig } from '../lib/supabase'
import { useShop } from '../contexts/ShopContext'
import { Link } from 'react-router-dom'
import {
  ArrowLeftIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  PlusIcon,
  TrashIcon,
  CogIcon,
  BoltIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline'
import CustomProcessorsTab from './custom/CustomProcessorsTab'
import { DEFAULT_SALES_CHANNELS, platformWithSalesChannels, savedSalesChannels, toggleSalesChannel, unknownSalesChannels } from '../lib/salesChannels'
import AutoTagsSettings from './AutoTagsSettings'
import { readAutoTagSettings, AutoTagSettings } from '../lib/autoTags'

// Multi-strategy pricing type
interface PricingStrategy {
  vat_rate: number;
  price_tiers: Array<{ min?: number; max?: number; method?: 'multiply' | 'add'; multiplier?: number; margin?: number }>;
  delivery_fee: number;
  rounding_rules: { round_to: string };
  global_discount_percent: number;
}

type FilterRule = { value: string; mode: 'contains' | 'exact' }

/**
 * Mirror of normalizeRules() in the workers' productFilters.ts, minus the lower-casing —
 * the dashboard shows what the merchant typed; matching is case-insensitive worker-side.
 * Accepts every stored shape: plain strings (all live entries), {value,mode} rules, and
 * the legacy {sku,title} pair.
 */
function parseRules(stored: any, fallbackMode: 'contains' | 'exact'): FilterRule[] {
  const listDefault: 'contains' | 'exact' =
    stored && !Array.isArray(stored) && (stored.mode === 'exact' || stored.mode === 'contains')
      ? stored.mode
      : fallbackMode
  const raw = Array.isArray(stored) ? stored : (Array.isArray(stored?.entries) ? stored.entries : [])
  const out: FilterRule[] = []
  for (const item of raw) {
    if (item == null) continue
    if (typeof item === 'string') {
      if (item.trim()) out.push({ value: item.trim(), mode: listDefault })
    } else if (typeof item === 'object') {
      if (typeof item.value === 'string' && item.value.trim()) {
        const mode = item.mode === 'exact' ? 'exact' : item.mode === 'contains' ? 'contains' : listDefault
        out.push({ value: item.value.trim(), mode })
      } else {
        if (typeof item.sku === 'string' && item.sku.trim()) out.push({ value: item.sku.trim(), mode: 'exact' })
        if (typeof item.title === 'string' && item.title.trim()) out.push({ value: item.title.trim(), mode: 'exact' })
      }
    }
  }
  return out
}

const ProcessorConfig: React.FC = () => {
  const { user, signOut } = useAuth()
  const toast = useToast()
  const { currentShopId } = useShop()
  const [shopConfig, setShopConfig] = useState<ShopConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [customProcessorsEnabled, setCustomProcessorsEnabled] = useState<boolean>(false)
  const [canUploadPremium, setCanUploadPremium] = useState<boolean>(false)

  // JSON view state
  const [jsonText, setJsonText] = useState<string>("")
  const copyHeader = (process.env.REACT_APP_CONFIG_COPY_HEADER || "").toString()

  // Form states for different processors
  const [titleBusinessRules, setTitleBusinessRules] = useState<Array<{ pattern: string, replacement: string }>>([])
  const [productTypeRules, setProductTypeRules] = useState<Array<{ pattern: string, replacement: string }>>([])
  const [categoryRules, setCategoryRules] = useState<Array<{ keywords?: string[], shopify_category_id?: string, product_type?: string }>>([])
  const [tagRules, setTagRules] = useState<Array<{ condition?: { keywords?: string[] }, tags?: string[] }>>([])
  // Multi-strategy pricing states
  const [pricingStrategies, setPricingStrategies] = useState<Record<string, PricingStrategy>>({});
  const [defaultPricingStrategy, setDefaultPricingStrategy] = useState<string>('standard');
  const [activeStrategyTab, setActiveStrategyTab] = useState<string>('standard');
  const [newStrategyName, setNewStrategyName] = useState<string>('');

  // Provider → pricing strategy mapping
  const [connectedProviders, setConnectedProviders] = useState<Array<{ id: string; name: string }>>([]);
  const [providerStrategyMap, setProviderStrategyMap] = useState<Record<string, string>>({});

  const [standardTags, setStandardTags] = useState<string[]>([])
  const [autoTags, setAutoTags] = useState<AutoTagSettings>(readAutoTagSettings({}))
  const [descriptionTemplate, setDescriptionTemplate] = useState('')
  const [productFeatures, setProductFeatures] = useState<string[]>([])

  // Translation processor states
  const [sizeName, setSizeName] = useState('')
  const [sizeMappings, setSizeMappings] = useState<{ [key: string]: string }>({})
  const [sizePrefix, setSizePrefix] = useState('')
  const [sizeSuffix, setSizeSuffix] = useState('')
  
  // Business rules toggles
  const [updateTitle, setUpdateTitle] = useState(false)
  const [updateBrand, setUpdateBrand] = useState(false)
  const [updatePrice, setUpdatePrice] = useState(false)
  const [updateCategory, setUpdateCategory] = useState(false)
  const [updateImage, setUpdateImage] = useState(false)
  const [updateProductType, setUpdateProductType] = useState(false)
  const [standardDescription, setStandardDescription] = useState(false)
  const [newProductStatus, setNewProductStatus] = useState<'ACTIVE' | 'DRAFT'>('DRAFT')
  // Sales channels new products are published to (W4). `availableChannels` null = not
  // loaded (or not Shopify); then the saved list is written back unchanged.
  const [salesChannels, setSalesChannels] = useState<string[]>(DEFAULT_SALES_CHANNELS)
  const [availableChannels, setAvailableChannels] = useState<string[] | null>(null)
  const [channelsError, setChannelsError] = useState<string | null>(null)
  // Written back only when changed here: the legacy hourly publish-all job reads the same
  // key and publishes to EVERY channel while it is absent, so saving an untouched default
  // would quietly narrow it for older stores.
  const [channelsTouched, setChannelsTouched] = useState(false)
  // business_rules.import_mode — creation only. Absent means in stock only (the workers'
  // default), so a store that never touches this keeps today's behaviour.
  const [importMode, setImportMode] = useState<'in_stock' | 'all'>('in_stock')
  // business_rules.require_photo_for_import (W3) — creation only, absent = off. Photos come from
  // any supplier with the same SKU (shared photo library), so this only holds back products
  // nobody has a photo for.
  const [requirePhoto, setRequirePhoto] = useState(false)
  // catalog.full_size_runs (W8) — creation only, absent = off. New products get every size of
  // the brand's usual run; sizes no supplier has are created sold out.
  const [fullSizeRuns, setFullSizeRuns] = useState(false)
  const [noPhoto, setNoPhoto] = useState<{ selected: number; missing: number; items: Array<{ sku: string; title: string | null }> } | null>(null)
  const [customMetafields, setCustomMetafields] = useState<Array<{ key: string, value: string }>>([])

  // Filters (Skip/Allow) lists
  const [allowedList, setAllowedList] = useState<string[]>([]) // UI uses strings, but saves as mixed types
  // One rules model over two stored lists:
  //   skip rules   -> business_rules.skip_list          (never sync; stock forced to 0)
  //   upload rules -> business_rules.creation_allow_list (creation-only keyword filter)
  // Both are matched by the same worker-side matcher, so a rule means the same thing
  // wherever it lives. `exact` skip rules are written back as plain strings, which is the
  // shape every one of the 3,461 live entries already has.
  const [skipRules, setSkipRules] = useState<Array<{ value: string; mode: 'contains' | 'exact' }>>([])
  const [uploadRules, setUploadRules] = useState<Array<{ value: string; mode: 'contains' | 'exact' }>>([])
  const [newRuleValue, setNewRuleValue] = useState('')
  const [newRuleMode, setNewRuleMode] = useState<'contains' | 'exact'>('exact')
  const [newRuleList, setNewRuleList] = useState<'skip' | 'upload'>('skip')

  type TaggedRule = FilterRule & { list: 'skip' | 'upload' }
  // Upload rules first. They are few and consequential, and a merchant adding their first
  // keyword to a 103-entry skip list would otherwise never see the new row — it lands at
  // the bottom, off-screen, and the add looks like it did nothing.
  const allRules: TaggedRule[] = [
    ...uploadRules.map((r) => ({ ...r, list: 'upload' as const })),
    ...skipRules.map((r) => ({ ...r, list: 'skip' as const })),
  ]

  const ruleExists = (value: string) =>
    allRules.some((r) => r.value.toLowerCase() === value.toLowerCase())

  const addRule = () => {
    const value = newRuleValue.trim()
    if (!value || ruleExists(value)) { setNewRuleValue(''); return }
    const rule = { value, mode: newRuleMode }
    if (newRuleList === 'skip') setSkipRules([...skipRules, rule])
    else setUploadRules([...uploadRules, rule])
    setNewRuleValue('')
  }

  const removeRule = (r: TaggedRule) => {
    if (r.list === 'skip') setSkipRules(skipRules.filter((x) => x.value !== r.value))
    else setUploadRules(uploadRules.filter((x) => x.value !== r.value))
  }

  const setRuleMode = (r: TaggedRule, mode: 'contains' | 'exact') => {
    const apply = (list: FilterRule[]) => list.map((x) => (x.value === r.value ? { ...x, mode } : x))
    if (r.list === 'skip') setSkipRules(apply(skipRules))
    else setUploadRules(apply(uploadRules))
  }

  // Moving a rule between lists is a delete plus an add; its match mode travels with it.
  const setRuleList = (r: TaggedRule, list: 'skip' | 'upload') => {
    if (list === r.list) return
    if (r.list === 'skip') {
      setSkipRules(skipRules.filter((x) => x.value !== r.value))
      setUploadRules([...uploadRules, { value: r.value, mode: r.mode }])
    } else {
      setUploadRules(uploadRules.filter((x) => x.value !== r.value))
      setSkipRules([...skipRules, { value: r.value, mode: r.mode }])
    }
  }
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'business' | 'pricing' | 'branding' | 'categories' | 'tags' | 'descriptions' | 'translations' | 'metafields' | 'filters' | 'json' | 'custom'>('business')

  // User role state for permission checks
  const [userRole, setUserRole] = useState<string | null>(null)
  const [isReadOnly, setIsReadOnly] = useState(false)

  useEffect(() => {
    fetchShopConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentShopId])

  useEffect(() => {
    if (!user || !currentShopId) return
    let cancelled = false
    setAvailableChannels(null)
    setChannelsError(null)
    supabase.functions
      .invoke('merchant-store-connection', { body: { shop_id: currentShopId, action: 'channels' } })
      .then(({ data, error }) => {
        if (cancelled) return
        if (error || !data?.ok) setChannelsError(data?.reason ?? 'Could not load your sales channels')
        else setAvailableChannels(data.channels as string[])
      })
    return () => { cancelled = true }
  }, [user, currentShopId])

  const fetchShopConfig = async () => {
    if (!user) return

    try {
      if (!currentShopId) {
        setShopConfig({} as ShopConfig)
        setCustomProcessorsEnabled(false)
        toast.error('No shop selected')
        return
      }

      const { data: configData, error: configError } = await supabase
        .from('merchant_config')
        .select('id, config_object')
        .eq('merchant_id', currentShopId)
        .single()

      // Read unified subscription row
      const { data: subData, error: subError } = await supabase
        .from('subscriptions')
        .select('plan_id, status, current_period_end, custom_processors_enabled')
        .eq('merchant_id', currentShopId)
        .maybeSingle()

      // Fetch user's role for this shop (maybeSingle handles no row gracefully)
      const { data: accessData, error: accessError } = await supabase
        .from('auth_users_shops')
        .select('role, status')
        .eq('shop_id', currentShopId)
        .eq('auth_user_id', user.id)
        .maybeSingle()

      if (accessError) {
        console.error('Error fetching user access:', accessError)
      }

      setUserRole(accessData?.role || null)
      // Explicit null check: no access data OR viewer role OR inactive status = read-only
      setIsReadOnly(!accessData || accessData.role === 'viewer' || accessData.status !== 'active')

      if (configError) {
        console.error('Error fetching config:', configError)
        toast.error('Failed to load configuration')
        setCustomProcessorsEnabled(false)
      } else if (configData) {
          const config = (configData as any).config_object as ShopConfig

          // Derive feature flags from subscriptions table
          const planId = (subData as any)?.plan_id as string | undefined
          const status = (subData as any)?.status as string | undefined
          const cpe = (subData as any)?.custom_processors_enabled as boolean | undefined
          const periodEnd = (subData as any)?.current_period_end as string | undefined
          const now = new Date()
          const notExpired = !periodEnd || new Date(periodEnd) > now
          const derivedFlag = status === 'active' && notExpired && (planId === 'premium' || planId === 'enterprise')

          // If mismatch, update subscriptions.custom_processors_enabled to derived state (authoritative)
          if ((cpe ?? false) !== derivedFlag && (subError == null)) {
            try {
              await supabase
                .from('subscriptions')
                .update({ custom_processors_enabled: derivedFlag, updated_at: new Date().toISOString() })
                .eq('merchant_id', currentShopId)
            } catch (e) {
              console.warn('Failed to sync subscriptions.custom_processors_enabled:', e)
            }
          }

          setCustomProcessorsEnabled((cpe ?? derivedFlag))
          // Enterprise-only uploads
          setCanUploadPremium(derivedFlag && planId === 'enterprise')
          if (config) {
            setShopConfig(config)
            try { setJsonText(JSON.stringify(config, null, 2)); } catch { setJsonText(""); }
          
            // Populate form fields
            setTitleBusinessRules(config.processors?.title?.businessRules || [])
            setProductTypeRules((config as any)?.processors?.productType?.businessRules || [])
            setCategoryRules(config.category_rules || [])
            setTagRules(config.tag_rules || [])
            // Load multi-strategy pricing (detect format)
            const pricingConfig = config.pricing as any;
            if (pricingConfig?.strategies) {
              // New multi-strategy format
              const strategies: Record<string, PricingStrategy> = {};
              for (const [name, strategy] of Object.entries(pricingConfig.strategies as Record<string, any>)) {
                strategies[name] = {
                  vat_rate: strategy.vat_rate ?? 0,
                  price_tiers: (strategy.price_tiers || []).map((t: any) => ({
                    min: t.min,
                    max: t.max,
                    method: t.method || (typeof t.multiplier === 'number' ? 'multiply' : 'add'),
                    multiplier: t.multiplier,
                    margin: t.margin,
                  })),
                  delivery_fee: strategy.delivery_fee ?? 0,
                  rounding_rules: strategy.rounding_rules || { round_to: 'to_1' },
                  global_discount_percent: strategy.global_discount_percent ?? 0,
                };
              }
              setPricingStrategies(strategies);
              setDefaultPricingStrategy(pricingConfig.default_strategy || Object.keys(strategies)[0] || 'standard');
              setActiveStrategyTab(pricingConfig.default_strategy || Object.keys(strategies)[0] || 'standard');
            } else if (pricingConfig) {
              // Legacy single-strategy format - convert to multi-strategy
              const legacyStrategy: PricingStrategy = {
                vat_rate: pricingConfig.vat_rate ?? 0,
                price_tiers: (pricingConfig.price_tiers || []).map((t: any) => ({
                  min: t.min,
                  max: t.max,
                  method: t.method || (typeof t.multiplier === 'number' ? 'multiply' : 'add'),
                  multiplier: t.multiplier,
                  margin: t.margin,
                })),
                delivery_fee: pricingConfig.delivery_fee ?? 0,
                rounding_rules: pricingConfig.rounding_rules || { round_to: 'to_1' },
                global_discount_percent: pricingConfig.global_discount_percent ?? 0,
              };
              setPricingStrategies({ standard: legacyStrategy });
              setDefaultPricingStrategy('standard');
              setActiveStrategyTab('standard');
            } else {
              // No pricing config - create empty default
              setPricingStrategies({
                standard: {
                  vat_rate: 0,
                  price_tiers: [],
                  delivery_fee: 0,
                  rounding_rules: { round_to: 'to_1' },
                  global_discount_percent: 0,
                }
              });
              setDefaultPricingStrategy('standard');
              setActiveStrategyTab('standard');
            }
            setStandardTags(config.branding?.standard_tags || [])
            setAutoTags(readAutoTagSettings(config))
            setDescriptionTemplate(config.description_template || '')
            setProductFeatures(config.product_features || [])
            
            // Translation processor
            setSizeName(config.translations?.size_name || '')
            setSizeMappings(config.translations?.size_mappings || {})
            setSizePrefix((config as any)?.translations?.size_value_prefix || '')
            setSizeSuffix((config as any)?.translations?.size_value_suffix || '')

            // Business rules
            setUpdateTitle(config.business_rules?.update_schema?.product?.title || false)
            setUpdateBrand(config.business_rules?.update_schema?.product?.brand || false)
            setUpdateCategory(config.business_rules?.update_schema?.product?.category || false)
            setUpdateImage(config.business_rules?.update_schema?.product?.image || false)
            setUpdateProductType((config as any)?.business_rules?.update_schema?.product?.productType || false)
            setUpdatePrice(config.business_rules?.update_schema?.variant?.price || false)
            setStandardDescription(config.business_rules?.update_schema?.clientConfig?.standardDescription || false)
            setNewProductStatus(((config as any)?.business_rules?.update_schema?.clientConfig?.newProductStatus as any) || 'DRAFT')
            setSalesChannels(savedSalesChannels(config))
            setChannelsTouched(false)
            setImportMode((config as any)?.business_rules?.import_mode === 'all' ? 'all' : 'in_stock')
            setRequirePhoto((config as any)?.business_rules?.require_photo_for_import === true)
            setFullSizeRuns(config.catalog?.full_size_runs === true)
            setCustomMetafields(((config as any)?.business_rules?.update_schema?.clientConfig?.customMetafields as any) || [])

            // Filters lists
            setSkipRules(parseRules(config.business_rules?.skip_list, 'exact'))
            setUploadRules(parseRules((config as any)?.business_rules?.creation_allow_list, 'contains'))
            // Handle allowed_list - it can be array of strings or objects with {title, sku} or {title, SKU}
            const allowedListData = config.business_rules?.allowed_list || []
            const allowedListStrings = allowedListData.map((item: any) => {
              if (typeof item === 'string') {
                return item
              } else if (item && typeof item === 'object') {
                // Backend expects {sku, title} but we might get {SKU, title}
                const sku = item.sku || item.SKU
                const title = item.title
                return title && sku ? `${title} (${sku})` : title || sku || String(item)
              }
              return String(item)
            })
            setAllowedList(allowedListStrings)
          }
        }
      else {
        setShopConfig({} as ShopConfig)
        setCustomProcessorsEnabled(false)
        toast.error('No configuration found. Please set up your configuration.')
      }

      // Fetch connected providers and their strategy mappings
      const { data: providerData } = await supabase
        .from('merchant_provider_map')
        .select('provider_id, capabilities, providers!inner(id, name)')
        .eq('merchant_id', currentShopId)

      if (providerData) {
        const providers = providerData.map((row: any) => ({
          id: row.provider_id,
          name: (row.providers as any)?.name || row.provider_id,
        }));
        setConnectedProviders(providers);

        const strategyMap: Record<string, string> = {};
        for (const row of providerData) {
          const caps = row.capabilities as any;
          if (caps?.default_pricing_strategy) {
            strategyMap[row.provider_id] = caps.default_pricing_strategy;
          }
        }
        setProviderStrategyMap(strategyMap);
      }
    } catch (error) {
      console.error('Error fetching shop config:', error)
      setCustomProcessorsEnabled(false)
      toast.error('Failed to load configuration. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Selected products that no supplier has a photo for (W3). Optional: hidden if the call fails.
  useEffect(() => {
    if (!currentShopId) return
    let cancelled = false
    supabase
      .rpc('get_merchant_products_without_photos', { p_merchant_id: currentShopId, p_limit: 20 })
      .then(({ data, error }) => {
        if (cancelled || error || !data) return
        const d = data as { selected_products: number; without_photo: number; items: Array<{ sku: string; title: string | null }> }
        setNoPhoto({ selected: d.selected_products, missing: d.without_photo, items: d.items || [] })
      })
    return () => { cancelled = true }
  }, [currentShopId])

  const handleSave = async () => {
    if (!user) return

    // Check if user has edit permissions
    if (isReadOnly) {
      toast.error('You do not have permission to edit configuration')
      return
    }

    if (!shopConfig) {
      toast.error('No configuration to save')
      return
    }

    setSaving(true)

    try {
      // Validate pricing strategies before save
      const strategyKeys = Object.keys(pricingStrategies);
      if (strategyKeys.length === 0) {
        toast.error('At least one pricing strategy is required');
        setSaving(false);
        return;
      }
      // Ensure default strategy exists
      const validDefaultStrategy = strategyKeys.includes(defaultPricingStrategy)
        ? defaultPricingStrategy
        : strategyKeys[0];

      // Build updated config with deep merge to preserve fields not controlled by UI
      const updatedConfig: ShopConfig = {
        ...shopConfig,  // Preserves: client_name, platform, etc.
        ...(channelsTouched ? { platform: platformWithSalesChannels(shopConfig.platform, salesChannels) } : {}),
        // Preserve processor_overrides if they exist
        processor_overrides: shopConfig.processor_overrides,
        // 1. DatabaseTitle processor - merge with existing
        processors: {
          ...(shopConfig.processors || {}),
          title: {
            ...(shopConfig.processors?.title || {}),
            businessRules: titleBusinessRules
          },
          productType: {
            ...(shopConfig.processors?.productType || {}),
            businessRules: productTypeRules
          }
        },
        // 2. DatabaseCategories processor
        category_rules: categoryRules,
        // 3. DatabaseDescription processor
        description_template: descriptionTemplate,
        product_features: productFeatures,
        // 4. DatabaseTags processor
        tag_rules: tagRules,
        // Automatic tags (W5): read by the sync workers via merchant_auto_tags
        auto_tags: autoTags,
        // Full size runs (W8): read by the sync workers when creating products
        catalog: {
          ...(shopConfig.catalog || {}),
          full_size_runs: fullSizeRuns,
        },
        // Merge branding to preserve other fields
        branding: {
          ...(shopConfig.branding || {}),
          standard_tags: standardTags
        },
        // 5. DatabasePricing processor (multi-strategy format only - don't spread legacy fields)
        pricing: {
          strategies: pricingStrategies,
          default_strategy: validDefaultStrategy,
        } as any,
        // 6. DatabaseTranslations processor - merge to preserve other translation fields
        translations: {
          ...(shopConfig.translations || {}),
          size_name: sizeName,
          size_mappings: sizeMappings,
          size_value_prefix: sizePrefix,
          size_value_suffix: sizeSuffix,
        },
        // 7 & 8. DatabaseProductType & DatabaseMetafields processors
        business_rules: {
          ...(shopConfig.business_rules || {}),  // Preserves: stock_providers, variant_sku_format, etc.
          // Exact rules go back as plain strings — the shape the rest of the system and
          // set_merchant_removed_products already handle. Only `contains` needs an object.
          skip_list: skipRules.map((r) => (r.mode === 'exact' ? r.value : { value: r.value, mode: r.mode })),
          creation_allow_list: uploadRules,
          import_mode: importMode,
          require_photo_for_import: requirePhoto,
          // Convert allowedList strings back to objects if they contain (SKU) pattern
          // Backend expects {sku, title} format (lowercase sku)
          allowed_list: allowedList.map(item => {
            const match = item.match(/^(.+)\s+\(([^)]+)\)$/)
            if (match) {
              return { title: match[1].trim(), sku: match[2].trim() }
            }
            return item
          }),
          // Deep merge update_schema to preserve fields not in UI
          update_schema: {
            ...(shopConfig.business_rules?.update_schema || {}),
            product: {
              ...(shopConfig.business_rules?.update_schema?.product || {}),  // Preserves unknown fields
              title: updateTitle,
              brand: updateBrand,
              image: updateImage,
              category: updateCategory,
              productType: updateProductType,
            },
            variant: {
              ...(shopConfig.business_rules?.update_schema?.variant || {}),  // Preserves: stock, etc.
              price: updatePrice,
            },
            clientConfig: {
              ...(shopConfig.business_rules?.update_schema?.clientConfig || {}),  // Preserves: skuInProductType, etc.
              standardTags: standardTags,
              standardDescription: standardDescription,
              newProductStatus: newProductStatus,
              customMetafields: customMetafields,
            }
          }
        }
      }

      // Update in database
      const { error } = await supabase
        .from('merchant_config')
        .update({
          config_object: updatedConfig,
          updated_at: new Date().toISOString()
        })
        .eq('merchant_id', currentShopId!)

      if (error) throw error

      // Save provider → pricing strategy mappings via RPC (safe — only touches default_pricing_strategy)
      for (const provider of connectedProviders) {
        // Fetch current to detect changes
        const { data: mpmRow } = await supabase
          .from('merchant_provider_map')
          .select('capabilities')
          .eq('merchant_id', currentShopId!)
          .eq('provider_id', provider.id)
          .single()

        const currentStrategy = (mpmRow?.capabilities as any)?.default_pricing_strategy || undefined;
        const strategy = providerStrategyMap[provider.id];

        if (strategy !== currentStrategy) {
          const { error: rpcError } = await supabase.rpc('set_provider_pricing_strategy', {
            p_merchant_id: currentShopId!,
            p_provider_id: provider.id,
            p_strategy: strategy || null,
          });
          if (rpcError) throw rpcError;
        }
      }

      toast.success('Configuration saved successfully!')
      setShopConfig(updatedConfig)
    } catch (error: any) {
      console.error('Error saving config:', error)
      toast.error(error.message || 'Failed to save configuration')
    } finally {
      setSaving(false)
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      // Navigate to login page after sign out
      window.location.href = '/'
    } catch (error) {
      console.error('Error signing out:', error)
      // Force redirect even if there's an error
      window.location.href = '/'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded animate-spin mx-auto mb-4"></div>
          <p className="text-white/50  text-sm">Loading configuration...</p>
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'business', label: 'BUSINESS LOGIC' },
    { id: 'pricing', label: 'PRICING' },
    { id: 'branding', label: 'BRANDING' },
    { id: 'categories', label: 'CATEGORIES' },
    { id: 'tags', label: 'TAGS' },
    { id: 'descriptions', label: 'DESCRIPTIONS' },
    { id: 'translations', label: 'TRANSLATIONS' },
    { id: 'metafields', label: 'METAFIELDS' },
    { id: 'filters', label: 'FILTERS' },
    { id: 'json', label: 'JSON' },
    { id: 'custom', label: 'CUSTOM PROCESSORS' },
  ] as const

  return (
    <div className="min-h-screen">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        {/* Read-Only Banner for Viewers */}
        {isReadOnly && (
          <div className="mb-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 flex items-center gap-3">
            <span className="text-xl">👁</span>
            <div>
              <p className="text-yellow-400  font-semibold text-sm">READ-ONLY MODE</p>
              <p className="text-yellow-700  text-xs">You have viewer access to this configuration. Contact an admin for edit permissions.</p>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-6 glass-card rounded-2xl">
          <nav className="flex" aria-label="Tabs">
            {tabs
              .filter(tab => tab.id !== 'custom' || customProcessorsEnabled)
              .map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`
                  flex-1 px-4 py-3 text-sm  font-semibold transition-all border-b-2
                  ${
                    activeTab === tab.id
                      ? 'bg-orange-500/10 text-orange-400 border-orange-500'
                      : 'text-white/50 hover:text-white hover:bg-white/[0.06] border-transparent'
                  }
                  ${index > 0 ? 'border-l border-white/[0.08]' : ''}
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Save Bar */}
        <div className="mb-6 glass-card rounded-2xl p-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
          </div>
          <button
            onClick={handleSave}
            disabled={saving || isReadOnly}
            className={`rounded-xl font-semibold py-2 px-6 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 ${
              isReadOnly
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-orange-500 hover:bg-orange-600 hover:shadow-lg text-white'
            }`}
          >
            <BoltIcon className="w-4 h-4" />
            <span>{isReadOnly ? 'VIEW ONLY' : (saving ? 'SAVING...' : 'SAVE CONFIG')}</span>
          </button>
        </div>

        {/* Tab Content */}
        <div className="glass-card rounded-2xl p-6">
          {/* JSON Tab */}
          {activeTab === 'json' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-white ">Configuration JSON</h2>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const payload = (copyHeader ? `${copyHeader}\n\n` : '') + jsonText;
                      await navigator.clipboard.writeText(payload);
                      toast.success('Configuration copied to clipboard');
                    } catch (e: any) {
                      toast.error(e?.message || 'Failed to copy');
                    }
                  }}
                  className="p-2 border border-white/15  hover:bg-white/[0.08] text-white/70"
                  title="Copy configuration"
                >
                  <DocumentDuplicateIcon className="w-5 h-5" />
                </button>
              </div>
              <div>
                <textarea
                  value={jsonText}
                  onChange={(e) => setJsonText(e.target.value)}
                  onPaste={async (e) => {
                    try {
                      const txt = e.clipboardData?.getData('text') || '';
                      const parsed = JSON.parse(txt);
                      // Auto-save pasted object immediately
                      if (!currentShopId) throw new Error('No shop selected');
                      const { error } = await supabase
                        .from('merchant_config')
                        .update({ config_object: parsed, updated_at: new Date().toISOString() })
                        .eq('merchant_id', currentShopId);
                      if (error) throw error;
                      setShopConfig(parsed);
                      setJsonText(JSON.stringify(parsed, null, 2));
                      toast.success('Configuration replaced and saved from pasted JSON');
                      // Prevent default paste to avoid duplicating content
                      e.preventDefault();
                    } catch (err) {
                      // If paste is not valid JSON, allow default paste behavior
                    }
                  }}
                  spellCheck={false}
                  className="w-full h-[480px]  text-sm border border-white/15 p-3  text-white focus:outline-none focus:ring-1 focus:ring-orange-500/30 focus:border-orange-500/50"
                  placeholder={`{\n  "processors": {}\n}`}
                />
                <p className="text-xs text-white/35  mt-1">Paste a full JSON object to auto-save. Copy button prepends custom header text to the clipboard.</p>
              </div>
            </div>
          )}

          {/* Business Logic Tab */}
          {activeTab === 'business' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white  mb-6">BUSINESS LOGIC CONFIGURATION</h2>

              <div className="bg-orange-500/10 border border-orange-500/20 p-4 mb-6">
                <p className="text-sm  text-white/70">
                  Update behavior: When a toggle is ON, that field will be updated on every sync. When OFF, it will only be updated when creating a brand new product (isNew).
                </p>
                <p className="text-xs  text-white/50 mt-1">
                  You can also use variables in text fields across processors: {'{name}'}, {'{brand}'}, {'{features}'}, and indexed features like {'{features-0}'}. Patterns support regular expressions.
                </p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Product Updates */}
                <div className="bg-transparent border border-white/[0.08] p-6">
                  <h3 className="text-base font-bold text-orange-400  mb-4">PRODUCT UPDATES</h3>
                  <div className="space-y-3">
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={updateTitle}
                        onChange={(e) => setUpdateTitle(e.target.checked)}
                        className="w-4 h-4 text-orange-400 focus:ring-primary-500 border-white/15"
                      />
                      <span className="text-white/70  text-sm">Update product titles</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={updateBrand}
                        onChange={(e) => setUpdateBrand(e.target.checked)}
                        className="w-4 h-4 text-orange-400 focus:ring-primary-500 border-white/15"
                      />
                      <span className="text-white/70  text-sm">Update product brands</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={updateCategory}
                        onChange={(e) => setUpdateCategory(e.target.checked)}
                        className="w-4 h-4 text-orange-400 focus:ring-primary-500 border-white/15"
                      />
                      <span className="text-white/70  text-sm">Update product categories</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={updateImage}
                        onChange={(e) => setUpdateImage(e.target.checked)}
                        className="w-4 h-4 text-orange-400 focus:ring-primary-500 border-white/15"
                      />
                      <span className="text-white/70  text-sm">Update product images</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={updateProductType}
                        onChange={(e) => setUpdateProductType(e.target.checked)}
                        className="w-4 h-4 text-orange-400 focus:ring-primary-500 border-white/15"
                      />
                      <span className="text-white/70  text-sm">Update product type</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={standardDescription}
                        onChange={(e) => setStandardDescription(e.target.checked)}
                        className="w-4 h-4 text-orange-400 focus:ring-primary-500 border-white/15"
                      />
                      <span className="text-white/70  text-sm">Use standard descriptions</span>
                    </label>
                  </div>
                </div>

                {/* Variant Updates */}
                <div className="bg-transparent border border-white/[0.08] p-6">
                  <h3 className="text-base font-bold text-orange-400  mb-4">VARIANT UPDATES</h3>
                  <div className="space-y-3">
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={updatePrice}
                        onChange={(e) => setUpdatePrice(e.target.checked)}
                        className="w-4 h-4 text-orange-400 focus:ring-primary-500 border-white/15"
                      />
                      <span className="text-white/70  text-sm">Update variant prices</span>
                    </label>
                  </div>
                </div>

                {/* Move: Standard Description into Product Updates */}
                {/* New Product Status */}
                <div className="bg-transparent border border-white/[0.08] p-6">
                  <h3 className="text-base font-bold text-orange-400  mb-4">NEW PRODUCT STATUS</h3>
                  <div className="flex items-center gap-4">
                    <label className="inline-flex items-center gap-2 text-sm  text-white/70">
                      <input
                        type="radio"
                        name="newProductStatus"
                        value="DRAFT"
                        checked={newProductStatus === 'DRAFT'}
                        onChange={() => setNewProductStatus('DRAFT')}
                        className="text-orange-400 border-white/15"
                      />
                      Draft
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm  text-white/70">
                      <input
                        type="radio"
                        name="newProductStatus"
                        value="ACTIVE"
                        checked={newProductStatus === 'ACTIVE'}
                        onChange={() => setNewProductStatus('ACTIVE')}
                        className="text-orange-400 border-white/15"
                      />
                      Active
                    </label>
                  </div>
                  <p className="text-xs text-white/35  mt-2">Applied only when creating new products.</p>
                </div>

                {/* Sales channels for new products (W4) */}
                <div className="bg-transparent border border-white/[0.08] p-6">
                  <h3 className="text-base font-bold text-orange-400  mb-4">PUBLISH NEW PRODUCTS TO</h3>
                  {availableChannels ? (
                    <div className="flex flex-wrap items-center gap-4">
                      {[...availableChannels, ...unknownSalesChannels(salesChannels, availableChannels)].map((name) => (
                        <label key={name} className="inline-flex items-center gap-2 text-sm  text-white/70">
                          <input
                            type="checkbox"
                            checked={salesChannels.includes(name)}
                            disabled={isReadOnly}
                            onChange={() => {
                              setSalesChannels(toggleSalesChannel(salesChannels, name))
                              setChannelsTouched(true)
                            }}
                            className="text-orange-400 border-white/15"
                          />
                          {name}
                          {!availableChannels.includes(name) && (
                            <span className="text-xs text-red-400">(not in your store)</span>
                          )}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-white/50">
                      {channelsError ? `${channelsError}. ` : 'Loading your sales channels… '}
                      Current: {salesChannels.join(', ')}
                    </p>
                  )}
                  <p className="text-xs text-white/35  mt-2">
                    Products FlowStock creates are published to these channels. A draft product is
                    published too, and shows up there as soon as it is set to Active.
                  </p>
                </div>

                {/* What to import (W7) */}
                <div className="bg-transparent border border-white/[0.08] p-6">
                  <h3 className="text-base font-bold text-orange-400  mb-4">WHAT TO IMPORT</h3>
                  <div className="flex flex-col gap-2">
                    <label className="inline-flex items-center gap-2 text-sm  text-white/70">
                      <input
                        type="radio"
                        name="importMode"
                        value="in_stock"
                        checked={importMode === 'in_stock'}
                        onChange={() => setImportMode('in_stock')}
                        className="text-orange-400 border-white/15"
                      />
                      Only products in stock
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm  text-white/70">
                      <input
                        type="radio"
                        name="importMode"
                        value="all"
                        checked={importMode === 'all'}
                        onChange={() => setImportMode('all')}
                        className="text-orange-400 border-white/15"
                      />
                      Everything, including sold-out products
                    </label>
                  </div>
                  <p className="text-xs text-white/35  mt-2">
                    Applied only when creating new products. With "Only products in stock", a selected product is added
                    to your store the first time a supplier has it in stock. Products already in your store are not affected.
                  </p>
                </div>

                {/* Full size runs (W8) */}
                <div className="bg-transparent border border-white/[0.08] p-6">
                  <h3 className="text-base font-bold text-orange-400  mb-4">SIZES</h3>
                  <label className="inline-flex items-center gap-2 text-sm  text-white/70">
                    <input
                      type="checkbox"
                      checked={fullSizeRuns}
                      onChange={(e) => setFullSizeRuns(e.target.checked)}
                      className="text-orange-400 border-white/15"
                    />
                    Create all sizes of the brand's size range
                  </label>
                  <p className="text-xs text-white/35  mt-2">
                    New products get every size of the brand's usual range, not only the sizes a supplier has.
                    Missing sizes are shown as sold out and can't be ordered. When a supplier gets one in stock,
                    it becomes buyable at the next sync. Applies to known shoe brands (Nike, Jordan, adidas, New Balance,
                    ASICS, Puma, Salomon, UGG, Converse, Vans) when the product's sizes are EU sizes; other products are
                    created as before. Products get about 4–5 times more variants, which counts towards Shopify's limit of
                    1,000 new variants a day for stores with more than 50,000 variants.
                    Applied only when creating new products.
                  </p>
                </div>

                {/* Products without photos (W3) */}
                <div className="bg-transparent border border-white/[0.08] p-6">
                  <h3 className="text-base font-bold text-orange-400  mb-4">PRODUCTS WITHOUT PHOTOS</h3>
                  <label className="inline-flex items-center gap-2 text-sm  text-white/70">
                    <input
                      type="checkbox"
                      checked={requirePhoto}
                      onChange={(e) => setRequirePhoto(e.target.checked)}
                      className="text-orange-400 border-white/15"
                    />
                    Don't import products until they have a photo
                  </label>
                  <p className="text-xs text-white/35  mt-2">
                    If a supplier has no photo for a product, FlowStock uses another supplier's photo of the same SKU.
                    This setting holds back only products no supplier has a photo for; they are added once one appears.
                    Applied only when creating new products.
                  </p>
                  {noPhoto && (
                    <div className="mt-4 text-sm text-white/70">
                      {noPhoto.missing === 0 ? (
                        <p>All {noPhoto.selected} selected products have a photo.</p>
                      ) : (
                        <details>
                          <summary className="cursor-pointer">
                            {noPhoto.missing} of {noPhoto.selected} selected products have no photo from any supplier
                          </summary>
                          <ul className="mt-2 space-y-1 text-xs text-white/50">
                            {noPhoto.items.map((i) => (
                              <li key={i.sku}>
                                <span className="font-mono">{i.sku}</span>{i.title ? ` — ${i.title}` : ''}
                              </li>
                            ))}
                            {noPhoto.missing > noPhoto.items.length && (
                              <li>…and {noPhoto.missing - noPhoto.items.length} more</li>
                            )}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}
                </div>

                {/* SKU & Metafields section removed as requested */}
              </div>

              {/* Title Processing Rules */}
              <div className="border-t border-white/[0.08] pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-bold text-orange-400 ">TITLE PROCESSING RULES</h3>
                  <button
                    onClick={() => setTitleBusinessRules([...titleBusinessRules, { pattern: '', replacement: '' }])}
                    className="px-4 py-2 bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/15 transition-colors flex items-center gap-2  text-sm"
                  >
                    <PlusIcon className="w-4 h-4" />
                    ADD RULE
                  </button>
                </div>
                <p className="text-xs text-white/35  mb-3">Use JavaScript regular expressions (flags: gi). Replacement supports variables like {'{name}'}, {'{brand}'}, {'{features}'}, {'{features-0}'} and capture groups ($1, $2...).</p>
                <div className="space-y-3">
                  {titleBusinessRules.map((rule, index) => (
                    <div key={index} className="bg-transparent border border-white/[0.08] p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm  text-white/70 mb-2">PATTERN (Regex)</label>
                          <input
                            type="text"
                            value={rule.pattern}
                            onChange={(e) => {
                              const updated = [...titleBusinessRules]
                              updated[index].pattern = e.target.value
                              setTitleBusinessRules(updated)
                            }}
                            placeholder="Text to find"
                            className="w-full px-3 py-2 border border-white/15  text-white  text-sm placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-orange-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm  text-white/70 mb-2">REPLACEMENT (supports variables)</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={rule.replacement}
                              onChange={(e) => {
                                const updated = [...titleBusinessRules]
                                updated[index].replacement = e.target.value
                                setTitleBusinessRules(updated)
                              }}
                              placeholder="Replace with"
                              className="flex-1 px-3 py-2 border border-white/15  text-white  text-sm placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-orange-500"
                            />
                            <button
                              onClick={() => setTitleBusinessRules(titleBusinessRules.filter((_, i) => i !== index))}
                              className="px-3 py-2 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-lg transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {titleBusinessRules.length === 0 && (
                    <div className="text-center py-8 text-white/25  text-sm">
                      No title processing rules configured
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Pricing Tab - Multi-Strategy */}
          {activeTab === 'pricing' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white  mb-6">PRICING CONFIGURATION</h2>

              {/* Default Strategy Selector */}
              <div className="bg-transparent border border-white/[0.08] p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <label className="text-sm  text-white/70">DEFAULT STRATEGY:</label>
                  <select
                    value={defaultPricingStrategy}
                    onChange={(e) => setDefaultPricingStrategy(e.target.value)}
                    className="px-3 py-1 border border-white/15  text-white  text-sm"
                  >
                    {Object.keys(pricingStrategies).map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-white/35 ">Used when product has no specific pricing_strategy</p>
              </div>

              {/* Provider → Strategy Mapping */}
              {connectedProviders.length > 0 && (
                <div className="bg-transparent border border-white/[0.08] p-4">
                  <h3 className="text-sm font-bold text-orange-400 mb-3">PROVIDER STRATEGY MAPPING</h3>
                  <p className="text-xs text-white/35 mb-4">
                    Assign a pricing strategy to each provider. Products from that provider will automatically use the assigned strategy. Leave as "default" to use the default strategy above.
                  </p>
                  <div className="space-y-2">
                    {connectedProviders.map((provider) => (
                      <div key={provider.id} className="flex items-center gap-3">
                        <span className="text-sm text-white/70 w-40 truncate" title={provider.name}>
                          {provider.name}
                        </span>
                        <select
                          value={providerStrategyMap[provider.id] || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setProviderStrategyMap((prev) => {
                              const next = { ...prev };
                              if (val) {
                                next[provider.id] = val;
                              } else {
                                delete next[provider.id];
                              }
                              return next;
                            });
                          }}
                          className="px-3 py-1 border border-white/15 text-white text-sm flex-1"
                        >
                          <option value="">default ({defaultPricingStrategy})</option>
                          {Object.keys(pricingStrategies).map((name) => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Strategy Tabs */}
              <div className="flex items-center gap-2 flex-wrap">
                {Object.keys(pricingStrategies).map((name) => (
                  <button
                    key={name}
                    onClick={() => setActiveStrategyTab(name)}
                    className={`px-4 py-2  text-sm border transition-colors ${
                      activeStrategyTab === name
                        ? 'bg-orange-500 text-white border-orange-500'
                        : ' text-white/70 border-white/15 hover:bg-white/[0.06]'
                    }`}
                  >
                    {name}
                    {name === defaultPricingStrategy && ' *'}
                  </button>
                ))}
                {/* Add New Strategy */}
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={newStrategyName}
                    onChange={(e) => setNewStrategyName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                    placeholder="new_strategy"
                    className="px-2 py-1 border border-white/15  text-white  text-sm w-32"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newStrategyName.trim() && !pricingStrategies[newStrategyName]) {
                        setPricingStrategies({
                          ...pricingStrategies,
                          [newStrategyName]: {
                            vat_rate: 0,
                            price_tiers: [],
                            delivery_fee: 0,
                            rounding_rules: { round_to: 'to_1' },
                            global_discount_percent: 0,
                          }
                        });
                        setActiveStrategyTab(newStrategyName);
                        setNewStrategyName('');
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      if (newStrategyName.trim() && !pricingStrategies[newStrategyName]) {
                        setPricingStrategies({
                          ...pricingStrategies,
                          [newStrategyName]: {
                            vat_rate: 0,
                            price_tiers: [],
                            delivery_fee: 0,
                            rounding_rules: { round_to: 'to_1' },
                            global_discount_percent: 0,
                          }
                        });
                        setActiveStrategyTab(newStrategyName);
                        setNewStrategyName('');
                      }
                    }}
                    disabled={!newStrategyName.trim() || !!pricingStrategies[newStrategyName]}
                    className="px-3 py-1 bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/15 transition-colors  text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PlusIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Active Strategy Content */}
              {pricingStrategies[activeStrategyTab] ? (
                <div key={activeStrategyTab} className="glass-card rounded-xl p-6">
                  {/* Strategy Header */}
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/[0.08]">
                    <h3 className="text-lg font-bold text-orange-400 ">
                      STRATEGY: {activeStrategyTab}
                      {activeStrategyTab === defaultPricingStrategy && (
                        <span className="ml-2 text-xs bg-orange-500/15 text-orange-500 px-2 py-1 rounded">DEFAULT</span>
                      )}
                    </h3>
                    {Object.keys(pricingStrategies).length > 1 && activeStrategyTab !== defaultPricingStrategy && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete strategy "${activeStrategyTab}"?`)) {
                            const { [activeStrategyTab]: _, ...rest } = pricingStrategies;
                            setPricingStrategies(rest);
                            setActiveStrategyTab(Object.keys(rest)[0]);
                          }
                        }}
                        className="px-3 py-1 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-lg transition-colors  text-sm flex items-center gap-2"
                      >
                        <TrashIcon className="w-4 h-4" />
                        DELETE STRATEGY
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Pricing Tiers */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-base font-bold text-white/70 ">PRICING TIERS</h4>
                        <button
                          onClick={() => {
                            const strategy = pricingStrategies[activeStrategyTab];
                            if (!strategy) return;
                            setPricingStrategies({
                              ...pricingStrategies,
                              [activeStrategyTab]: {
                                ...strategy,
                                price_tiers: [...(strategy.price_tiers || []), { min: 0, max: 100, method: 'multiply', multiplier: 1.5 }]
                              }
                            });
                          }}
                          className="px-3 py-1 bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/15 transition-colors flex items-center gap-2  text-xs"
                        >
                          <PlusIcon className="w-3 h-3" />
                          ADD
                        </button>
                      </div>
                      <div className="space-y-3">
                        {(pricingStrategies[activeStrategyTab]?.price_tiers || []).map((tier, index) => (
                          <div key={index} className="bg-transparent border border-white/[0.08] p-4">
                            <div className="grid grid-cols-5 gap-3">
                              <div>
                                <label className="block text-xs  text-white/50 mb-1">MIN</label>
                                <input
                                  type="number"
                                  value={tier.min ?? 0}
                                  onChange={(e) => {
                                    const strategy = pricingStrategies[activeStrategyTab];
                                    if (!strategy?.price_tiers) return;
                                    const newTiers = strategy.price_tiers.map((t, i) =>
                                      i === index ? { ...t, min: parseFloat(e.target.value) || 0 } : t
                                    );
                                    setPricingStrategies({
                                      ...pricingStrategies,
                                      [activeStrategyTab]: { ...strategy, price_tiers: newTiers }
                                    });
                                  }}
                                  className="w-full px-2 py-1 border border-white/15  text-white  text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs  text-white/50 mb-1">MAX</label>
                                <input
                                  type="number"
                                  value={tier.max ?? 0}
                                  onChange={(e) => {
                                    const strategy = pricingStrategies[activeStrategyTab];
                                    if (!strategy?.price_tiers) return;
                                    const newTiers = strategy.price_tiers.map((t, i) =>
                                      i === index ? { ...t, max: parseFloat(e.target.value) || 0 } : t
                                    );
                                    setPricingStrategies({
                                      ...pricingStrategies,
                                      [activeStrategyTab]: { ...strategy, price_tiers: newTiers }
                                    });
                                  }}
                                  className="w-full px-2 py-1 border border-white/15  text-white  text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs  text-white/50 mb-1">METHOD</label>
                                <select
                                  value={tier.method || 'multiply'}
                                  onChange={(e) => {
                                    const strategy = pricingStrategies[activeStrategyTab];
                                    if (!strategy?.price_tiers) return;
                                    const newTiers = strategy.price_tiers.map((t, i) =>
                                      i === index ? { ...t, method: e.target.value as 'multiply' | 'add' } : t
                                    );
                                    setPricingStrategies({
                                      ...pricingStrategies,
                                      [activeStrategyTab]: { ...strategy, price_tiers: newTiers }
                                    });
                                  }}
                                  className="w-full px-2 py-1 border border-white/15  text-white  text-sm"
                                >
                                  <option value="multiply">Multiply</option>
                                  <option value="add">Add fixed</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs  text-white/50 mb-1">VALUE</label>
                                <div className="flex gap-1">
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={(tier.method || 'multiply') === 'multiply' ? (tier.multiplier ?? 1) : (tier.margin ?? 0)}
                                    onChange={(e) => {
                                      const strategy = pricingStrategies[activeStrategyTab];
                                      if (!strategy?.price_tiers) return;
                                      const val = parseFloat(e.target.value) || ((tier.method || 'multiply') === 'multiply' ? 1 : 0);
                                      const newTiers = strategy.price_tiers.map((t, i) => {
                                        if (i !== index) return t;
                                        return (t.method || 'multiply') === 'multiply'
                                          ? { ...t, multiplier: val }
                                          : { ...t, margin: val };
                                      });
                                      setPricingStrategies({
                                        ...pricingStrategies,
                                        [activeStrategyTab]: { ...strategy, price_tiers: newTiers }
                                      });
                                    }}
                                    className="flex-1 px-2 py-1 border border-white/15  text-white  text-sm"
                                  />
                                  <button
                                    onClick={() => {
                                      const strategy = pricingStrategies[activeStrategyTab];
                                      if (!strategy?.price_tiers) return;
                                      const newTiers = strategy.price_tiers.filter((_, i) => i !== index);
                                      setPricingStrategies({
                                        ...pricingStrategies,
                                        [activeStrategyTab]: { ...strategy, price_tiers: newTiers }
                                      });
                                    }}
                                    className="px-2 py-1 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-lg transition-colors"
                                  >
                                    <TrashIcon className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {(pricingStrategies[activeStrategyTab]?.price_tiers?.length || 0) === 0 && (
                          <div className="text-center py-4 text-white/25  text-sm">
                            No pricing tiers configured
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Additional Pricing Settings */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm  text-white/70 mb-2">VAT RATE (%)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={pricingStrategies[activeStrategyTab]?.vat_rate ?? 0}
                          onChange={(e) => {
                            const strategy = pricingStrategies[activeStrategyTab];
                            if (!strategy) return;
                            setPricingStrategies({
                              ...pricingStrategies,
                              [activeStrategyTab]: { ...strategy, vat_rate: parseFloat(e.target.value) || 0 }
                            });
                          }}
                          placeholder="21 for 21%"
                          className="w-full px-3 py-2 border border-white/15  text-white  placeholder-white/20"
                        />
                      </div>
                      <div>
                        <label className="block text-sm  text-white/70 mb-2">DELIVERY FEE ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={pricingStrategies[activeStrategyTab]?.delivery_fee ?? 0}
                          onChange={(e) => {
                            const strategy = pricingStrategies[activeStrategyTab];
                            if (!strategy) return;
                            setPricingStrategies({
                              ...pricingStrategies,
                              [activeStrategyTab]: { ...strategy, delivery_fee: parseFloat(e.target.value) || 0 }
                            });
                          }}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-white/15  text-white  placeholder-white/20"
                        />
                      </div>
                      <div>
                        <label className="block text-sm  text-white/70 mb-2">GLOBAL DISCOUNT %</label>
                        <input
                          type="number"
                          step="0.1"
                          value={pricingStrategies[activeStrategyTab]?.global_discount_percent ?? 0}
                          onChange={(e) => {
                            const strategy = pricingStrategies[activeStrategyTab];
                            if (!strategy) return;
                            setPricingStrategies({
                              ...pricingStrategies,
                              [activeStrategyTab]: { ...strategy, global_discount_percent: parseFloat(e.target.value) || 0 }
                            });
                          }}
                          placeholder="0 (no discount)"
                          className="w-full px-3 py-2 border border-white/15  text-white  placeholder-white/20"
                        />
                      </div>
                      <div>
                        <label className="block text-sm  text-white/70 mb-2">ROUND PRICES TO</label>
                        <select
                          value={pricingStrategies[activeStrategyTab]?.rounding_rules?.round_to || 'to_1'}
                          onChange={(e) => {
                            if (!pricingStrategies[activeStrategyTab]) return;
                            setPricingStrategies({
                              ...pricingStrategies,
                              [activeStrategyTab]: { ...pricingStrategies[activeStrategyTab], rounding_rules: { round_to: e.target.value } }
                            });
                          }}
                          className="w-full px-3 py-2 border border-white/15  text-white "
                        >
                          <option value="to_0.01">No rounding (0.01)</option>
                          <option value="to_0.05">Nearest 0.05</option>
                          <option value="to_0.10">Nearest 0.10</option>
                          <option value="to_0.25">Nearest 0.25</option>
                          <option value="to_0.50">Nearest 0.50</option>
                          <option value="to_1">Nearest dollar</option>
                          <option value="to_5">Nearest $5</option>
                          <option value="to_10">Nearest $10</option>
                          <option value="ending_99">.99 ending</option>
                          <option value="ending_x9_99">x9.99 ending, nearest (99.99, 129.99, etc.)</option>
                          <option value="ceil_x9_99">x9.99 ending, always UP (never below cost)</option>
                          <option value="to_90">.90 ending (round to $5)</option>
                        </select>
                      </div>
                      <div className="bg-orange-500/10 border border-orange-500/20 p-4">
                        <p className="text-xs  text-white/50">PRICE FORMULA:</p>
                        <p className="text-sm text-orange-500  mt-1">
                          Tier: Multiply → (Cost × Multiplier) OR Add fixed → (Cost + Margin)
                        </p>
                        <p className="text-sm text-orange-500 ">
                          Then: × (1 + VAT) + Delivery → Round → Apply Global Discount (if any)
                        </p>
                        <p className="text-xs  text-white/50 mt-2">Rounded to: {pricingStrategies[activeStrategyTab]?.rounding_rules?.round_to}</p>
                        <p className="text-xs  text-white/50 mt-2">
                          Global discount applies across all prices: CompareAt = Rounded base; Price = CompareAt × (1 - Discount%) rounded.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-6 text-center">
                  <p className="text-yellow-700 ">
                    Strategy "{activeStrategyTab}" not found. Available: {Object.keys(pricingStrategies).join(', ') || 'none'}
                  </p>
                  <button
                    onClick={() => {
                      const firstStrategy = Object.keys(pricingStrategies)[0];
                      if (firstStrategy) setActiveStrategyTab(firstStrategy);
                    }}
                    className="mt-3 px-4 py-2 bg-yellow-600 text-white  text-sm hover:bg-yellow-700"
                  >
                    Reset to first strategy
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Branding Tab */}
          {activeTab === 'branding' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white  mb-6">BRANDING CONFIGURATION</h2>

              <div className="bg-orange-500/10 border border-orange-500/20 p-4 mb-6">
                <p className="text-sm  text-white/70">
                  Configure general branding settings for your store. For tag management, use the TAGS tab.
                </p>
              </div>

              <div className="bg-transparent border border-white/[0.08] p-6">
                <h3 className="text-base font-bold text-orange-400  mb-4">STORE BRANDING</h3>
                <p className="text-sm  text-white/50">Additional branding configuration options will be added here in future releases.</p>
              </div>
            </div>
          )}

          {/* Categories Tab - DatabaseCategories processor */}
          {activeTab === 'categories' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white  mb-6">CATEGORY RULES</h2>
              
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-orange-400 ">CATEGORY MAPPING RULES</h3>
                <button
                  onClick={() => setCategoryRules([...categoryRules, { keywords: [], shopify_category_id: '', product_type: '' }])}
                  className="px-4 py-2 bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/15 transition-colors flex items-center gap-2  text-sm"
                >
                  <PlusIcon className="w-4 h-4" />
                  ADD RULE
                </button>
              </div>
              
              <div className="space-y-4">
                {categoryRules.map((rule, index) => (
                  <div key={index} className="bg-transparent border border-white/[0.08] p-4">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm  text-white/70 mb-2">KEYWORDS (comma-separated)</label>
                        <input
                          type="text"
                          value={rule.keywords?.join(', ') || ''}
                          onChange={(e) => {
                            const updated = [...categoryRules]
                            updated[index].keywords = e.target.value.split(',').map(k => k.trim()).filter(k => k)
                            setCategoryRules(updated)
                          }}
                          placeholder="sneaker, shoe, footwear"
                          className="w-full px-3 py-2 border border-white/15  text-white  placeholder-white/20"
                        />
                        <p className="text-xs text-white/35  mt-1">Keywords to match in product name or brand</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm  text-white/70 mb-2">PRODUCT TYPE</label>
                          <input
                            type="text"
                            value={rule.product_type || ''}
                            onChange={(e) => {
                              const updated = [...categoryRules]
                              updated[index].product_type = e.target.value
                              setCategoryRules(updated)
                            }}
                            placeholder="Footwear"
                            className="w-full px-3 py-2 border border-white/15  text-white "
                          />
                        </div>
                        <div>
                          <label className="block text-sm  text-white/70 mb-2">SHOPIFY CATEGORY ID</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={rule.shopify_category_id || ''}
                              onChange={(e) => {
                                const updated = [...categoryRules]
                                updated[index].shopify_category_id = e.target.value
                                setCategoryRules(updated)
                              }}
                              placeholder="gid://shopify/..."
                              className="flex-1 px-3 py-2 border border-white/15  text-white  text-sm"
                            />
                            <button
                              onClick={() => setCategoryRules(categoryRules.filter((_, i) => i !== index))}
                              className="px-3 py-2 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-lg transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {categoryRules.length === 0 && (
                  <div className="bg-transparent border border-white/[0.08] p-8 text-center">
                    <p className="text-white/25  text-sm">No category rules configured</p>
                    <p className="text-white/35  text-xs mt-2">Add rules to automatically categorize products based on keywords</p>
                  </div>
                )}
              </div>

              <div className="bg-orange-500/10 border border-orange-500/20 p-4">
                <p className="text-xs  text-white/50 mb-2">HOW IT WORKS:</p>
                <p className="text-sm  text-white/70">Rules are applied in order. First matching rule wins.</p>
                <p className="text-sm  text-white/70">Keywords are matched against product name and brand (case-insensitive).</p>
              </div>
            </div>
          )}

          {/* Tags Tab - DatabaseTags processor with variable support */}
          {activeTab === 'tags' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white  mb-6">TAGS CONFIGURATION</h2>

              <div className="bg-orange-500/10 border border-orange-500/20 p-4 mb-6">
                <p className="text-sm  text-white/70 mb-2">
                  Configure tags that will be applied to your products. All tag values support variables like {'{brand}'}, {'{name}'}, {'{features}'}, and {'{features-0}'}.
                </p>
                <p className="text-xs  text-white/50">
                  Standard tags are applied to all products. Tag rules are applied conditionally based on keywords matching product name or brand.
                </p>
              </div>

              <AutoTagsSettings value={autoTags} onChange={setAutoTags} />

              {/* Standard Tags */}
              <div className="glass-card rounded-xl p-6">
                <h3 className="text-base font-bold text-orange-400  mb-4">STANDARD TAGS</h3>
                <p className="text-sm  text-white/50 mb-4">These tags are applied to all products. Variables will be resolved for each product.</p>

                <div className="flex flex-wrap gap-2 mb-4">
                  {standardTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-orange-500/10 text-orange-500 border border-orange-500/20  text-sm"
                    >
                      {tag}
                      <button
                        onClick={() => setStandardTags(standardTags.filter((_, i) => i !== index))}
                        className="text-orange-400 hover:text-red-400 transition-colors"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add a tag (e.g., '{brand} Product', 'Premium {features-0}')"
                    className="flex-1 px-4 py-2 border border-white/15  text-white  placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-orange-500"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                        setStandardTags([...standardTags, e.currentTarget.value.trim()])
                        e.currentTarget.value = ''
                      }
                    }}
                  />
                  <button
                    onClick={(e) => {
                      const input = e.currentTarget.previousElementSibling as HTMLInputElement
                      if (input.value.trim()) {
                        setStandardTags([...standardTags, input.value.trim()])
                        input.value = ''
                      }
                    }}
                    className="px-4 py-2 bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/15 transition-colors "
                  >
                    <PlusIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Tag Rules */}
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-bold text-orange-400 ">CONDITIONAL TAG RULES</h3>
                  <button
                    onClick={() => setTagRules([...tagRules, { condition: { keywords: [] }, tags: [] }])}
                    className="px-4 py-2 bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/15 transition-colors flex items-center gap-2  text-sm"
                  >
                    <PlusIcon className="w-4 h-4" />
                    ADD RULE
                  </button>
                </div>
                <p className="text-sm  text-white/50 mb-4">Apply specific tags when products match certain keywords. Each rule can add multiple tags.</p>

                <div className="space-y-4">
                  {tagRules.map((rule, index) => (
                    <div key={index} className="bg-transparent border border-white/[0.08] p-4">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm  text-white/70 mb-2">KEYWORDS (comma-separated)</label>
                          <input
                            type="text"
                            value={rule.condition?.keywords?.join(', ') || ''}
                            onChange={(e) => {
                              const updated = [...tagRules]
                              if (!updated[index].condition) updated[index].condition = {}
                              updated[index].condition!.keywords = e.target.value.split(',').map(k => k.trim()).filter(k => k)
                              setTagRules(updated)
                            }}
                            placeholder="sneaker, nike, running"
                            className="w-full px-3 py-2 border border-white/15  text-white  placeholder-white/20"
                          />
                          <p className="text-xs text-white/35  mt-1">Keywords to match in product name or brand (case-insensitive)</p>
                        </div>

                        <div>
                          <label className="block text-sm  text-white/70 mb-2">TAGS TO APPLY</label>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {(rule.tags || []).map((tag, tagIndex) => (
                              <span
                                key={tagIndex}
                                className="inline-flex items-center gap-2 px-3 py-1  border border-white/15  text-sm"
                              >
                                {tag}
                                <button
                                  onClick={() => {
                                    const updated = [...tagRules]
                                    if (updated[index].tags) {
                                      updated[index].tags = updated[index].tags!.filter((_, i) => i !== tagIndex)
                                    }
                                    setTagRules(updated)
                                  }}
                                  className="text-red-400 hover:text-red-400 transition-colors"
                                >
                                  <TrashIcon className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>

                          <div className="flex gap-2 mb-3">
                            <input
                              type="text"
                              placeholder="Add tag (supports variables: '{brand} Collection', '{features-0}')"
                              className="flex-1 px-3 py-2 border border-white/15  text-white  text-sm placeholder-white/20"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                                  const updated = [...tagRules]
                                  if (!updated[index].tags) updated[index].tags = []
                                  updated[index].tags!.push(e.currentTarget.value.trim())
                                  setTagRules(updated)
                                  e.currentTarget.value = ''
                                }
                              }}
                            />
                            <button
                              onClick={(e) => {
                                const input = e.currentTarget.previousElementSibling as HTMLInputElement
                                if (input.value.trim()) {
                                  const updated = [...tagRules]
                                  if (!updated[index].tags) updated[index].tags = []
                                  updated[index].tags!.push(input.value.trim())
                                  setTagRules(updated)
                                  input.value = ''
                                }
                              }}
                              className="px-3 py-2 bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/15 transition-colors"
                            >
                              <PlusIcon className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="flex justify-end">
                            <button
                              onClick={() => setTagRules(tagRules.filter((_, i) => i !== index))}
                              className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-lg transition-colors flex items-center gap-2  text-sm"
                            >
                              <TrashIcon className="w-4 h-4" />
                              REMOVE RULE
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {tagRules.length === 0 && (
                    <div className="bg-transparent border border-white/[0.08] p-8 text-center">
                      <p className="text-white/25  text-sm">No tag rules configured</p>
                      <p className="text-white/35  text-xs mt-2">Add rules to automatically tag products based on keywords</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Variable Examples */}
              <div className="bg-orange-500/10 border border-orange-500/20 p-4">
                <h4 className="text-sm  text-white font-bold mb-2">VARIABLE EXAMPLES:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs ">
                  <div>
                    <p className="text-white/50 mb-1">STANDARD VARIABLES:</p>
                    <div className="space-y-1">
                      <div><code className=" px-1">{'{brand}'}</code> → Nike</div>
                      <div><code className=" px-1">{'{name}'}</code> → Air Max 90</div>
                      <div><code className=" px-1">{'{brand} Shoes'}</code> → Nike Shoes</div>
                    </div>
                  </div>
                  <div>
                    <p className="text-white/50 mb-1">FEATURE VARIABLES:</p>
                    <div className="space-y-1">
                      <div><code className=" px-1">{'{features-0}'}</code> → First feature</div>
                      <div><code className=" px-1">{'{features-1}'}</code> → Second feature</div>
                      <div><code className=" px-1">Premium {'{features-0}'}</code> → Premium Quality</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Descriptions Tab - DatabaseDescription processor */}
          {activeTab === 'descriptions' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white  mb-6">DESCRIPTION CONFIGURATION</h2>
              
              <div>
                <label className="block text-sm  text-white/70 mb-2">
                  DESCRIPTION TEMPLATE
                </label>
                <textarea
                  value={descriptionTemplate}
                  onChange={(e) => setDescriptionTemplate(e.target.value)}
                  placeholder="<p>High-quality {brand} product: {name}</p><p>Features:</p>{features}"
                  rows={8}
                  className="w-full px-4 py-3 border border-white/15  text-white  text-sm placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-orange-500 resize-none"
                />
                <p className="text-xs text-white/35  mt-2">Available variables: {'{brand}'}, {'{name}'}, {'{features}'}, {'{features-0}'} (first item), {'{features-1}'} (second item), etc.</p>
              </div>

              <div>
                <label className="block text-sm  text-white/70 mb-4">PRODUCT FEATURES</label>
                <div className="bg-transparent border border-white/[0.08] p-4">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {productFeatures.map((feature, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-2 px-3 py-1  border border-white/15  text-sm"
                      >
                        {feature}
                        <button
                          onClick={() => setProductFeatures(productFeatures.filter((_, i) => i !== index))}
                          className="text-red-400 hover:text-red-400 transition-colors"
                        >
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add a product feature"
                      className="flex-1 px-4 py-2 border border-white/15  text-white  placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-orange-500"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                          setProductFeatures([...productFeatures, e.currentTarget.value.trim()])
                          e.currentTarget.value = ''
                        }
                      }}
                    />
                    <button
                      onClick={(e) => {
                        const input = e.currentTarget.previousElementSibling as HTMLInputElement
                        if (input.value.trim()) {
                          setProductFeatures([...productFeatures, input.value.trim()])
                          input.value = ''
                        }
                      }}
                      className="px-4 py-2 bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/15 transition-colors "
                    >
                      <PlusIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-white/35  mt-2">Features will be inserted as bullet points when {'{features}'} is used</p>
                </div>
              </div>

              <div className="bg-orange-500/10 border border-orange-500/20 p-4">
                <p className="text-xs  text-white/50 mb-2">PREVIEW:</p>
                <div className=" border border-orange-500/30 p-3 mt-2 text-sm  text-white/70">
                  {(() => {
                    const featuresList = productFeatures.length > 0 ? productFeatures : ['Premium quality','Durable design'];
                    const replaceVars = (tpl: string) => {
                      // {features-n}
                      let out = tpl.replace(/\{features-(\d+)\}/g, (_m, idx) => {
                        const i = parseInt(idx, 10);
                        return featuresList[i] || '';
                      });
                      // {features}
                      out = out.replace(/\{features\}/g, `<ul>${featuresList.map(f => `<li>${f}</li>`).join('')}</ul>`);
                      // basic vars
                      out = out.replace(/\{brand\}/g, 'Nike').replace(/\{name\}/g, 'Air Max 90');
                      return out;
                    };
                    const rawHtml = descriptionTemplate ? replaceVars(descriptionTemplate) : '<p>High-quality Nike product: Air Max 90</p>';
                    const html = DOMPurify.sanitize(rawHtml);
                    return (
                      <div dangerouslySetInnerHTML={{ __html: html }} />
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Product Type Rules Tab Section (inside Business tab) */}
          {activeTab === 'business' && (
            <div className="border-t border-white/[0.08] pt-6 mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-orange-400 ">PRODUCT TYPE RULES</h3>
                <button
                  onClick={() => setProductTypeRules([...productTypeRules, { pattern: '', replacement: '' }])}
                  className="px-4 py-2 bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/15 transition-colors flex items-center gap-2  text-sm"
                >
                  <PlusIcon className="w-4 h-4" />
                  ADD RULE
                </button>
              </div>
              <p className="text-xs text-white/35  mb-3">Regex pattern and replacement to build product type from product text. You can also use variables like {'{name}'} and {'{brand}'}.</p>
              <div className="space-y-3">
                {productTypeRules.map((rule, index) => (
                  <div key={index} className="bg-transparent border border-white/[0.08] p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm  text-white/70 mb-2">PATTERN (Regex)</label>
                        <input
                          type="text"
                          value={rule.pattern}
                          onChange={(e) => {
                            const updated = [...productTypeRules]
                            updated[index].pattern = e.target.value
                            setProductTypeRules(updated)
                          }}
                          placeholder="e.g., .+"
                          className="w-full px-3 py-2 border border-white/15  text-white  text-sm placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm  text-white/70 mb-2">REPLACEMENT</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={rule.replacement}
                            onChange={(e) => {
                              const updated = [...productTypeRules]
                              updated[index].replacement = e.target.value
                              setProductTypeRules(updated)
                            }}
                            placeholder="e.g., Footwear / $1 / {brand}"
                            className="flex-1 px-3 py-2 border border-white/15  text-white  text-sm placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-orange-500"
                          />
                          <button
                            onClick={() => setProductTypeRules(productTypeRules.filter((_, i) => i !== index))}
                            className="px-3 py-2 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-lg transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {productTypeRules.length === 0 && (
                  <div className="text-center py-8 text-white/25  text-sm">
                    No product type rules configured
                  </div>
                )}
              </div>
              <div className="bg-orange-500/10 border border-orange-500/20 p-4 mt-4">
                <p className="text-xs  text-white/50">Tip: Leave pattern empty to set a constant product type from the replacement.</p>
              </div>
            </div>
          )}

          {/* Translations Tab - DatabaseTranslations processor */}
          {activeTab === 'translations' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white  mb-6">SIZE TRANSLATIONS</h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm  text-white/70 mb-2">SIZE OPTION NAME</label>
                  <input
                    type="text"
                    value={sizeName}
                    onChange={(e) => setSizeName(e.target.value)}
                    placeholder="Size"
                    className="w-full px-3 py-2 border border-white/15  text-white "
                  />
                  <p className="text-xs text-white/35  mt-1">The name of the size option (e.g., "Size", "Taille", "Größe")</p>
                </div>
                
                <div className="bg-orange-500/10 border border-orange-500/20 p-4">
                  <p className="text-xs  text-white/50">INFO:</p>
                  <p className="text-sm  text-white/70">Translations are applied to variant options</p>
                  <p className="text-sm  text-white/70 mt-1">Maps size codes to readable names</p>
                </div>
              </div>
              
              {/* Size value formatting */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2">
                <div>
                  <label className="block text-sm  text-white/70 mb-2">SIZE VALUE PREFIX</label>
                  <input
                    type="text"
                    value={sizePrefix}
                    onChange={(e) => setSizePrefix(e.target.value)}
                    placeholder="e.g., EU "
                    className="w-full px-3 py-2 border border-white/15  text-white "
                  />
                  <p className="text-xs text-white/35  mt-1">Added before each size value (idempotent)</p>
                </div>
                <div>
                  <label className="block text-sm  text-white/70 mb-2">SIZE VALUE SUFFIX</label>
                  <input
                    type="text"
                    value={sizeSuffix}
                    onChange={(e) => setSizeSuffix(e.target.value)}
                    placeholder="e.g., cm"
                    className="w-full px-3 py-2 border border-white/15  text-white "
                  />
                  <p className="text-xs text-white/35  mt-1">Added after each size value (idempotent)</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm  text-white/70 mb-4">SIZE MAPPINGS</label>
                <div className="bg-transparent border border-white/[0.08] p-4">
                  <div className="space-y-2 mb-4">
                    {Object.entries(sizeMappings).map(([from, to], index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={from}
                          placeholder="From (e.g., XS)"
                          className="flex-1 px-3 py-2 border border-white/15 bg-transparent text-white/70 "
                          readOnly
                        />
                        <span className="text-white/35 ">→</span>
                        <input
                          type="text"
                          value={to}
                          onChange={(e) => {
                            const updated = {...sizeMappings}
                            updated[from] = e.target.value
                            setSizeMappings(updated)
                          }}
                          placeholder="To (e.g., Extra Small)"
                          className="flex-1 px-3 py-2 border border-white/15  text-white "
                        />
                        <button
                          onClick={() => {
                            const updated = {...sizeMappings}
                            delete updated[from]
                            setSizeMappings(updated)
                          }}
                          className="px-3 py-2 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-lg transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="border-t border-white/15 pt-4">
                    <p className="text-xs  text-white/50 mb-2">ADD NEW MAPPING</p>
                    <div className="flex gap-2 items-center">
                      <input
                        type="text"
                        placeholder="Size code (e.g., XS)"
                        id="new-size-from"
                        className="flex-1 px-3 py-2 border border-white/15  text-white "
                      />
                      <span className="text-white/35 ">→</span>
                      <input
                        type="text"
                        placeholder="Translation (e.g., Extra Small)"
                        id="new-size-to"
                        className="flex-1 px-3 py-2 border border-white/15  text-white "
                      />
                      <button
                        onClick={() => {
                          const fromInput = document.getElementById('new-size-from') as HTMLInputElement
                          const toInput = document.getElementById('new-size-to') as HTMLInputElement
                          if (fromInput.value && toInput.value) {
                            setSizeMappings({...sizeMappings, [fromInput.value]: toInput.value})
                            fromInput.value = ''
                            toInput.value = ''
                          }
                        }}
                        className="px-4 py-2 bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/15 transition-colors "
                      >
                        ADD
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-transparent border border-white/[0.08] p-4">
                  <p className="text-xs  text-white/50 mb-2">COMMON SIZE MAPPINGS</p>
                  <div className="space-y-1 text-sm ">
                    <div className="flex justify-between">
                      <span className="text-white/50">XS</span>
                      <span className="text-white">Extra Small</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50">S</span>
                      <span className="text-white">Small</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50">M</span>
                      <span className="text-white">Medium</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50">L</span>
                      <span className="text-white">Large</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50">XL</span>
                      <span className="text-white">Extra Large</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-transparent border border-white/[0.08] p-4">
                  <p className="text-xs  text-white/50 mb-2">NUMERIC SIZE EXAMPLES</p>
                  <div className="space-y-1 text-sm ">
                    <div className="flex justify-between">
                      <span className="text-white/50">36</span>
                      <span className="text-white">Size 36 (EU)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50">38</span>
                      <span className="text-white">Size 38 (EU)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50">40</span>
                      <span className="text-white">Size 40 (EU)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50">42</span>
                      <span className="text-white">Size 42 (EU)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/50">44</span>
                      <span className="text-white">Size 44 (EU)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters Tab - Skip/Allow Lists */}
          {activeTab === 'filters' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white mb-2">FILTERS</h2>
              <p className="text-sm text-white/50 mb-4">
                One list of rules controlling what FlowStock syncs. Each rule says what to match and what to do about it.
              </p>

              <div className="glass-card rounded-xl p-4">
                {/* Add a rule */}
                <div className="flex flex-col md:flex-row gap-2 mb-4">
                  <input
                    type="text"
                    value={newRuleValue}
                    onChange={(e) => setNewRuleValue(e.target.value)}
                    placeholder="SKU or product name, e.g. DD1391-100 or Asics Kayano 14"
                    className="flex-1 px-4 py-2 border border-white/15 text-white placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-orange-500"
                    onKeyDown={(e) => { if (e.key === 'Enter') addRule() }}
                  />
                  <select
                    value={newRuleMode}
                    onChange={(e) => setNewRuleMode(e.target.value as 'contains' | 'exact')}
                    className="px-3 py-2 border border-white/15 text-white bg-transparent text-sm"
                    title="Exact name matches the whole SKU or product name. Contains matches it as a word anywhere in the name."
                  >
                    <option className="bg-neutral-900" value="exact">Exact name</option>
                    <option className="bg-neutral-900" value="contains">Contains</option>
                  </select>
                  <select
                    value={newRuleList}
                    onChange={(e) => setNewRuleList(e.target.value as 'skip' | 'upload')}
                    className="px-3 py-2 border border-white/15 text-white bg-transparent text-sm"
                  >
                    <option className="bg-neutral-900" value="skip">Never sync</option>
                    <option className="bg-neutral-900" value="upload">Only upload these</option>
                  </select>
                  <button
                    onClick={addRule}
                    disabled={!newRuleValue.trim()}
                    className="px-4 py-2 bg-orange-500 hover:bg-orange-400 text-black text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Add rule
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5 text-xs text-white/40">
                  <div className="border border-white/[0.08] p-3">
                    <span className="text-red-400 font-semibold">Never sync</span> — FlowStock leaves these
                    products alone entirely and sets their stock to 0. Use it to take something off your store.
                  </div>
                  <div className="border border-white/[0.08] p-3">
                    <span className="text-emerald-400 font-semibold">Only upload these</span> — when any such
                    rule exists, FlowStock uploads only matching products, from every supplier. Products already
                    on your store keep syncing either way.
                  </div>
                </div>

                {/* The rules */}
                {allRules.length === 0 ? (
                  <div className="text-sm text-white/30 py-3">
                    No rules yet — every product is synced and eligible for upload.
                  </div>
                ) : (
                  <div className="divide-y divide-white/[0.06]">
                    {allRules.map((r) => (
                      <div key={`${r.list}-${r.value}`} className="flex flex-wrap items-center gap-2 py-2">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${
                          r.list === 'skip'
                            ? 'bg-red-500/10 border-red-500/20 text-red-400'
                            : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                        }`}>
                          {r.list === 'skip' ? 'Never sync' : 'Only upload'}
                        </span>
                        <span className="text-sm text-white/80 font-mono truncate flex-1 min-w-[10rem]">{r.value}</span>
                        <select
                          value={r.mode}
                          onChange={(e) => setRuleMode(r, e.target.value as 'contains' | 'exact')}
                          className="px-2 py-1 border border-white/15 text-white bg-transparent text-xs flex-shrink-0"
                        >
                          <option className="bg-neutral-900" value="exact">Exact name</option>
                          <option className="bg-neutral-900" value="contains">Contains</option>
                        </select>
                        <select
                          value={r.list}
                          onChange={(e) => setRuleList(r, e.target.value as 'skip' | 'upload')}
                          className="px-2 py-1 border border-white/15 text-white bg-transparent text-xs flex-shrink-0"
                        >
                          <option className="bg-neutral-900" value="skip">Never sync</option>
                          <option className="bg-neutral-900" value="upload">Only upload these</option>
                        </select>
                        <button
                          onClick={() => removeRule(r)}
                          className="text-white/30 hover:text-red-400 text-xs px-2 flex-shrink-0"
                          aria-label={`Remove rule ${r.value}`}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 text-xs text-white/35">
                  <span className="text-white/50">Contains</span> matches whole words only, so &ldquo;UGG&rdquo;
                  will not pull in &ldquo;Rugged&rdquo;. It does match longer names, so &ldquo;Asics Kayano
                  14&rdquo; also covers &ldquo;Asics Kayano 14 Premium&rdquo; &mdash; use
                  <span className="text-white/50"> Exact name</span> for just the one model.
                  A <span className="text-red-400">Never sync</span> rule always beats an
                  <span className="text-emerald-400"> Only upload</span> one.
                </div>
              </div>

            </div>
          )}

          {/* Metafields Tab */}
          {activeTab === 'metafields' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white  mb-2">CUSTOM METAFIELDS</h2>
              <p className="text-sm text-white/50  mb-4">Add custom metafields (key and value). Note: The metafield must already exist in your Shopify store (be defined) for updates to apply.</p>
              <div className="space-y-3">
                {customMetafields.map((mf, index) => (
                  <div key={index} className="bg-transparent border border-white/[0.08] p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                      <div>
                        <label className="block text-sm  text-white/70 mb-2">KEY</label>
                        <input
                          type="text"
                          value={mf.key}
                          onChange={(e) => {
                            const updated = [...customMetafields]
                            updated[index].key = e.target.value
                            setCustomMetafields(updated)
                          }}
                          placeholder="e.g., material"
                          className="w-full px-3 py-2 border border-white/15  text-white  text-sm"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm  text-white/70 mb-2">VALUE</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={mf.value}
                            onChange={(e) => {
                              const updated = [...customMetafields]
                              updated[index].value = e.target.value
                              setCustomMetafields(updated)
                            }}
                            placeholder="e.g., Leather"
                            className="flex-1 px-3 py-2 border border-white/15  text-white  text-sm"
                          />
                          <button
                            onClick={() => setCustomMetafields(customMetafields.filter((_, i) => i !== index))}
                            className="px-3 py-2 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-lg transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {customMetafields.length === 0 && (
                  <div className="text-center py-8 text-white/25  text-sm">No custom metafields yet</div>
                )}
              </div>
              <div>
                <button
                  onClick={() => setCustomMetafields([...customMetafields, { key: '', value: '' }])}
                  className="px-4 py-2 bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/15 transition-colors flex items-center gap-2  text-sm"
                >
                  <PlusIcon className="w-4 h-4" />
                  ADD METAFIELD
                </button>
              </div>
              <div className="bg-orange-500/10 border border-orange-500/20 p-4">
                <p className="text-xs  text-white/50">We will write custom values to your metafields using the default namespace and string type. Ensure the metafield definition exists in your store admin.</p>
              </div>
            </div>
          )}

          {/* Custom Processors Tab - Upload and Preview .ts files */}
          {activeTab === 'custom' && customProcessorsEnabled && (
            <CustomProcessorsTab canUpload={canUploadPremium} />
          )}
        </div>
      </main>
    </div>
  )
}

export default ProcessorConfig
